<template>
  <div class="invest-page">
    <div class="background-header"></div>
    <div class="shell">
      <app-topbar @lang-change="lang = $event" />

      <!-- Invest Input Card -->
      <section class="invest-card">
        <div class="invest-card-inner">
          <div class="invest-label">{{ t.enterAmount }}</div>
          <div class="invest-input-row">
            <input class="invest-input" type="number" v-model="investAmount" placeholder="0.000" />
            <span class="invest-unit">USDT</span>
          </div>
          <div class="invest-btn">
            <div class="invest-range">
              <span>{{ t.min }} 100U</span>
              <span>{{ t.max }} 10,000U</span>
            </div>
            <div class="quick-amounts">
              <button
                class="quick-btn"
                :class="{ 'quick-btn--active': investAmount === '100' }"
                @click="investAmount = '100'"
              >
                100U
              </button>
              <button
                class="quick-btn"
                :class="{ 'quick-btn--active': investAmount === '500' }"
                @click="investAmount = '500'"
              >
                500U
              </button>
              <button
                class="quick-btn"
                :class="{ 'quick-btn--active': investAmount === '1000' }"
                @click="investAmount = '1000'"
              >
                1000U
              </button>
              <button
                class="quick-btn"
                :class="{ 'quick-btn--active': investAmount === '10000' }"
                @click="investAmount = '10000'"
              >
                10000U
              </button>
            </div>
          </div>
          <div class="invest-desc">{{ t.investDesc }}</div>
          <button class="connect-btn" type="button" :disabled="investing" @click="onConfirm">
            <img class="connect-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
            <span class="connect-btn-text" v-if="!investing">{{ t.confirmInvest }}</span>
            <span class="connect-btn-text" v-else>Processing...</span>
          </button>
        </div>
      </section>

      <!-- My Orders -->
      <section class="orders-section">
        <div class="orders-title">
          {{ t.myOrders }}
          <span class="orders-loading" v-if="loadingOrders">
            <svg class="spin-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </span>
        </div>
        <div class="order-list">
          <div class="order-card" v-for="order in orders" :key="order.id">
            <div class="order-card-inner">
              <div class="order-header">
                <div class="order-id">{{ t.order }} #OC-{{ String(order.id).padStart(4, '0') }}</div>
                <div class="order-status" :class="'order-status--' + order.status">
                  {{ order.statusText }}
                </div>
              </div>
              <div class="order-info">
                {{ t.principal }} {{ order.principal }} USDT · {{ t.dailyYield }} {{ dailyRate }}
              </div>
              <div class="order-countdown">
                <div class="countdown-icon">
                  <svg
                    width="23"
                    height="23"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div class="countdown-text">
                  <div class="countdown-label">{{ t.cycleCountdown }}</div>
                  <div class="countdown-value">{{ order.countdownValue }}</div>
                </div>
                <div class="countdown-arrow" v-if="order.status === 'running'">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <!-- <polyline points="9 18 15 12 9 6"></polyline> -->
                  </svg>
                </div>
              </div>
              <div class="order-actions" v-if="order.status === 'expired'">
                <button class="action-btn action-btn--primary" @click="onReinvest(order)">{{ t.fullReinvest }}</button>
                <button class="action-btn action-btn--secondary" @click="onRedeem(order)">{{ t.redeemExit }}</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <bottom-nav active-tab="invest" />

    <!-- Redeem Confirmation Modal -->
    <div class="modal-mask" v-if="redeemModalVisible" @click.self="cancelRedeem">
      <div class="modal-card">
        <div class="modal-title">{{ t.redeemExit }}</div>
        <div class="modal-detail">
          <div class="detail-row">
            <span class="detail-label">{{ t.applyAmount }}</span>
            <span class="detail-value">{{ redeemApplyAmount }} USDT</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t.receiveRatio }}</span>
            <span class="detail-value">{{ withdrawRatio !== null ? withdrawRatio + '%' : '--' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t.estimatedReceipt }}</span>
            <span class="detail-value">{{ redeemEstimated }} {{ t.equivalentOC }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t.forceReinvest }}</span>
            <span class="detail-value">{{ redeemForceReinvest }} USDT</span>
          </div>
        </div>
        <div class="modal-actions">
          <button class="modal-btn modal-btn--cancel" type="button" @click="cancelRedeem">{{ t.cancel }}</button>
          <button class="modal-btn modal-btn--confirm" type="button" :disabled="redeeming" @click="onConfirmRedeem">
            <img class="modal-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
            <span class="modal-btn-text" v-if="!redeeming">{{ t.confirmRedeem }}</span>
            <span class="modal-btn-text" v-else>Processing...</span>
          </button>
        </div>
      </div>
    </div>

    <div class="toast" v-if="toastVisible">{{ toastMsg }}</div>
  </div>
</template>

<script>
import AppTopbar from '@/components/AppTopbar.vue'
import BottomNav from '@/components/BottomNav.vue'
import { wallet } from '@/store/wallet'
import { getMessages } from '@/i18n'
import { ethers } from 'ethers'
import { getOrders, syncOrders, reinvest, redeemOrder, getLpTiers } from '@/api/auth'
import OpenClawInvestAbi from '@/abi/OpenClawInvest.json'
import OpenClawAbi from '@/abi/OpenClaw.json'
import RouterAbi from '@/abi/Router.json'
import UsdtAbi from '@/abi/usdt_abi.json'

const OPENCLAW_INVEST_ADDRESS = process.env.VUE_APP_OPENCLAW_INVEST_CONTRACT_ADDRESS
const USDT_ADDRESS = process.env.VUE_APP_USDT_ADDRESS
const OC_ADDRESS = process.env.VUE_APP_OC_ADDRESS
const ALK_ADDRESS = process.env.VUE_APP_ALK_ADDRESS
const ROUTER_ADDRESS = process.env.VUE_APP_ROUTER_ADDRESS
const RPC_URL = process.env.VUE_APP_NEXT_PUBLIC_NODE_1

export default {
  name: 'InvestPage',
  components: { AppTopbar, BottomNav },
  data() {
    return {
      lang: localStorage.getItem('lang') || 'en',
      investAmount: '100.000',
      investing: false,
      toastVisible: false,
      toastMsg: '',
      orders: [],
      loadingOrders: false,
      dailyRate: '--',
      withdrawRatio: null,
      reinvestRatio: null,
      ocPrice: null,
      redeemModalVisible: false,
      redeemingOrder: null,
      redeeming: false,
    }
  },
  computed: {
    t() {
      return getMessages(this.lang)
    },
    walletAddress() {
      return wallet.address
    },
    isLoggedIn() {
      return wallet.isLoggedIn
    },
    redeemApplyAmount() {
      return this.redeemingOrder ? this.redeemingOrder.principal : '--'
    },
    redeemEstimated() {
      if (!this.redeemingOrder || this.withdrawRatio === null) return '--'
      const usdt = parseFloat(this.redeemingOrder.principal)
      if (!usdt) return '--'
      return (usdt * this.withdrawRatio / 100).toFixed(2)
    },
    redeemForceReinvest() {
      if (!this.redeemingOrder || this.reinvestRatio === null) return '--'
      const usdt = parseFloat(this.redeemingOrder.principal)
      if (!usdt) return '--'
      return (usdt * this.reinvestRatio / 100).toFixed(2)
    },
  },
  watch: {
    walletAddress(addr) {
      this.orders = []
      if (addr && wallet.isLoggedIn) this.loadOrders()
    },
    isLoggedIn(val) {
      if (val) this.loadOrders()
    },
  },
  mounted() {
    this.lang = localStorage.getItem('lang') || 'en'
    this.fetchDailyRate()
    this.fetchOcPrice()
    if (this.walletAddress && wallet.isLoggedIn) this.loadOrders()
  },
  methods: {
    changeLang(lang) {
      this.lang = lang
      localStorage.setItem('lang', lang)
    },
    async fetchDailyRate() {
      try {
        const [tiersRes, lpDepthUsdt] = await Promise.all([getLpTiers(), this.fetchLpDepth()])
        const tiers = tiersRes.data
        const tier = tiers.find(t => lpDepthUsdt >= parseFloat(t.min_lp) && (t.max_lp === null || lpDepthUsdt < parseFloat(t.max_lp)))
        if (tier) {
          this.dailyRate = (parseFloat(tier.daily_rate) * 100).toFixed(2) + '%'
          this.withdrawRatio = tier.withdraw_ratio
          this.reinvestRatio = tier.reinvest_ratio
        }
      } catch (e) {
        console.error('fetchDailyRate failed', e)
      }
    },
    async fetchOcPrice() {
      if (!OC_ADDRESS || !ALK_ADDRESS || !USDT_ADDRESS || !ROUTER_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const router = new ethers.Contract(ROUTER_ADDRESS, RouterAbi, provider)
        const amounts = await router.getAmountsOut(ethers.parseUnits('1', 18), [OC_ADDRESS, ALK_ADDRESS, USDT_ADDRESS])
        this.ocPrice = parseFloat(ethers.formatUnits(amounts[2], 18))
      } catch (e) {
        console.error('fetchOcPrice failed', e)
      }
    },
    async fetchLpDepth() {
      if (!OC_ADDRESS || !ALK_ADDRESS || !USDT_ADDRESS || !ROUTER_ADDRESS) return 0
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const oc = new ethers.Contract(OC_ADDRESS, OpenClawAbi, provider)
        const alk = new ethers.Contract(ALK_ADDRESS, UsdtAbi, provider)
        const router = new ethers.Contract(ROUTER_ADDRESS, RouterAbi, provider)
        const pairAddress = await oc.pairAddress()
        const [ocReserve, alkReserve, ocAmounts, alkAmounts] = await Promise.all([
          oc.balanceOf(pairAddress),
          alk.balanceOf(pairAddress),
          router.getAmountsOut(ethers.parseUnits('1', 18), [OC_ADDRESS, ALK_ADDRESS, USDT_ADDRESS]),
          router.getAmountsOut(ethers.parseUnits('1', 18), [ALK_ADDRESS, USDT_ADDRESS]),
        ])
        const ocPrice = parseFloat(ethers.formatUnits(ocAmounts[2], 18))
        const alkPrice = parseFloat(ethers.formatUnits(alkAmounts[1], 18))
        const lpDepth = parseFloat(ethers.formatUnits(ocReserve, 18)) * ocPrice
        console.log('[fetchLpDepth] ocReserve:', ethers.formatUnits(ocReserve, 18), 'ocPrice:', ocPrice, 'lpDepth:', lpDepth)
        return lpDepth
      } catch (e) {
        console.error('fetchLpDepth failed', e)
        return 0
      }
    },
    async loadOrders() {
      this.loadingOrders = true
      try {
        const res = await getOrders()
        const now = Date.now()
        this.orders = res.data.map(o => {
          const isExpired = o.status === '已到期'
          const endTime = o.end_time ? new Date(o.end_time).getTime() : null
          const daysLeft = endTime && endTime > now ? Math.ceil((endTime - now) / 86400000) : 0
          return {
            id: parseInt(o.order_id.replace('#OC-', '')),
            principal: parseFloat(o.usdt_amount).toFixed(2),
            status: isExpired ? 'expired' : 'running',
            statusText: isExpired ? this.t.expired : this.t.running,
            countdownValue: isExpired ? this.t.expiredProcess : (daysLeft + this.t.daysLeft),
          }
        })
      } catch (e) {
        console.error('loadOrders failed', e)
      } finally {
        this.loadingOrders = false
      }
    },
    async onConfirm() {
      if (this.investing) return
      if (!this.walletAddress) {
        alert(this.t.connectWallet)
        return
      }
      const amount = parseFloat(this.investAmount)
      if (!amount || amount < 100 || amount > 10000) {
        this.showToast('100 ~ 10,000 USDT')
        return
      }
      this.investing = true
      try {
        const provider = new ethers.BrowserProvider(window.ethereum)
        const signer = await provider.getSigner()
        const amountWei = ethers.parseUnits(String(amount), 18)

        const usdt = new ethers.Contract(USDT_ADDRESS, UsdtAbi, signer)
        const approveTx = await usdt.approve(OPENCLAW_INVEST_ADDRESS, amountWei, { gasLimit: 100000 })
        await approveTx.wait()

        const contract = new ethers.Contract(OPENCLAW_INVEST_ADDRESS, OpenClawInvestAbi, signer)
        const tx = await contract.invest(amountWei, { gasLimit: 2000000 })
        await tx.wait()

        this.showToast(this.t.confirmInvest + ' ✓')
        try { await syncOrders() } catch (e) { /* ignore */ }
        await new Promise(r => setTimeout(r, 1500))
        this.loadOrders()
      } catch (e) {
        this.showToast(e.reason || e.message || 'Error')
      } finally {
        this.investing = false
      }
    },
    async onReinvest(order) {
      try {
        await reinvest(order.id)
        this.showToast(this.t.fullReinvest + ' ✓')
        this.loadOrders()
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error
        this.showToast(msg || 'Error')
      }
    },
    onRedeem(order) {
      this.redeemingOrder = order
      this.redeemModalVisible = true
    },
    cancelRedeem() {
      this.redeemModalVisible = false
      this.redeemingOrder = null
    },
    async onConfirmRedeem() {
      if (this.redeeming || !this.redeemingOrder) return
      this.redeeming = true
      try {
        await redeemOrder(this.redeemingOrder.id)
        this.showToast(this.t.redeemExit + ' ✓')
        this.redeemModalVisible = false
        this.redeemingOrder = null
        this.loadOrders()
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error
        this.showToast(msg || 'Error')
      } finally {
        this.redeeming = false
      }
    },
    showToast(msg) {
      this.toastMsg = msg
      this.toastVisible = true
      setTimeout(() => {
        this.toastVisible = false
      }, 2500)
    }
  }
}
</script>

<style lang="less" scoped>
.invest-page {
  min-height: 100vh;
  color: #fff;
  background-image: url(../assets/home/background.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  padding-bottom: 120px;
  position: relative;
}
.background-header {
  width: 100%;
  height: 200px;
  background-image: url(../assets/home/background-header.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.shell {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 15px 24px;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

// ---- Invest Card ----
.invest-card {
  margin-top: 12px;
  border-radius: 10px;
  padding: 1px;
  background-image: url(../assets/home/Invest-border.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  position: relative;
}

.invest-card-inner {
  border-radius: 10px;
  padding: 20px 16px 18px;
}

.invest-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.invest-input-row {
  display: flex;
  align-items: baseline;
  margin-top: 10px;
  gap: 8px;
}

.invest-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  font-family: 'DIN Alternate', sans-serif;
  line-height: 1;
  width: 100%;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }
}

.invest-unit {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  flex-shrink: 0;
}

.invest-btn {
  width: 100%;
  min-height: 100px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
}

.invest-range {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 1);
}

.quick-amounts {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}

.quick-btn {
  flex: 1;
  height: 36px;
  border-radius: 6px;
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &--active {
    background: linear-gradient(135deg, #e91e4d 0%, #c2185b 100%);
    color: #fff;
  }
}

.invest-desc {
  height: 50px;
  margin-top: 14px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 6px;
  line-height: 1.6;
}

.connect-btn {
  margin-top: 20px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.connect-btn-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.connect-btn-text {
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

// ---- Orders Section ----
.orders-section {
  margin-top: 24px;
}

.orders-title {
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.orders-loading {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.45);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin-icon {
  animation: spin 0.8s linear infinite;
}

.order-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-card {
  border-radius: 10px;
  padding: 1px;
  background-image: url(../assets/home/level-border.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
}

.order-card-inner {
  border-radius: 10px;
  // background: rgba(20, 10, 18, 0.8);
  padding: 16px;
}

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.order-id {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.order-status {
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 90px;

  &--running {
    color: #14d990;
    background: rgba(20, 217, 144, 0.1);
  }

  &--expired {
    color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.06);
  }
}

.order-info {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.order-countdown {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.countdown-icon {
  width: 23px;
  height: 32px;
  border-radius: 50%;
  // border: 1.5px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.countdown-text {
  flex: 1;
}

.countdown-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
}

.countdown-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
  white-space: pre-line;
  line-height: 1.4;
}

.countdown-arrow {
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.order-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
}

.action-btn {
  flex: 1;
  height: 40px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &--primary {
    // background: linear-gradient(135deg, #e91e4d 0%, #c2185b 100%);
    background-image: url(../assets/home/all-btn.png);
    background-size: 100% 100%;
    background-repeat: no-repeat;
    color: #fff;
  }

  &--secondary {
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid #ed233c;
  }

  &:active {
    opacity: 0.85;
  }
}

// ---- Redeem Modal ----
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
  padding: 0 20px;
}

.modal-card {
  width: 100%;
  max-width: 390px;
  background: linear-gradient(180deg, #1a0510 0%, #11050f 100%);
  border: 1px solid rgba(243, 40, 90, 0.3);
  border-radius: 12px;
  padding: 20px 16px;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  margin-bottom: 16px;
}

.modal-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.modal-detail .detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}

.modal-detail .detail-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.modal-detail .detail-value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  text-align: right;
}

.modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.modal-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  font-size: 15px;
  font-weight: 500;

  &--cancel {
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  &--confirm {
    color: rgba(255, 255, 255, 0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.modal-btn-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.modal-btn-text {
  position: relative;
  z-index: 1;
}

// ---- Toast ----
.toast {
  position: fixed;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
}
</style>
